import React from "react";
import { Navigate, RouteObject, createBrowserRouter } from "react-router-dom";
import { RootNode } from "./root-node";
import pages from "@components/pages";
import { NavigationWrapper } from "@components/templates/layouts/navigation-wrapper/navigation-wrapper";
import { AuthGuard } from "@src/auth/auth-guard";
import { LanguageProvider } from "@src/language/language-context";
import Taskbar from "@src/components/organisms/taskbar/taskbar";
import { DASHBOARD_BY_ID_PATH, getDashboardPath } from "./dashboard-paths";

export { getDashboardPath };

export enum Route {
  THINGS_OS_HOMEPAGE = "/",
  LOGIN = "/login",
  LOGOUT = "/logout",
  DASHBOARD_BUILDER = "/dashboard-builder",
  DASHBOARD = "/dashboard",
  GATEWAYS = "/gateways",
  SOURCES_LIST_EMPTY_PAGE = "/sources-empty",
  NO_SOURCES_DETECTED_PAGE = "/no-sources-detected",
  NO_GATEWAYS_DETECTED_PAGE = "/no-gateways-detected",
  SERVER_ERROR = "/server-error",
  PERMISSION_DENIED = "/permission-missing",
  TOKENS_EXPIRED_ERROR = "/tokens-expired",
  SPINNER_PAGE = "/spinner-page",
  NODE_MANAGER_PAGE = "/nodemanager",
  NODE_MANAGER_DASHBOARD_PAGE_OLD = "/nodemanager/dashboard_old",
  BLUEPRINT_DASHBOARD_PAGE = "/nodemanager/dashboard",
  CONFIGURATION_MANAGER_PAGE = "/configuration-manager",
  USER_MANAGEMENT_PAGE = "/user-management",
  MACHINES = "/machines",
  TICKETS_PAGE = "/tickets",
  VNC = "/remote-service/:sessionId",
  FACTORY_DASHBOARD = "/factory-dashboard",
  ADMIN_PANEL = "/admin-panel",
}

const protectedRoutes: RouteObject[] = [
  {
    path: Route.THINGS_OS_HOMEPAGE,
    element: <pages.ThingsOSHomepage />,
  },
  {
    path: Route.GATEWAYS,
    element: <pages.GatewaysAndSourcesListPage />,
  },
  {
    path: Route.SERVER_ERROR,
    element: <pages.ServerErrorPage />,
  },
  {
    path: Route.PERMISSION_DENIED,
    element: <pages.PermissionMissing />,
  },
  {
    path: Route.TOKENS_EXPIRED_ERROR,
    element: <pages.RefreshTokenExpiredErrorPage />,
  },
  {
    path: Route.SPINNER_PAGE,
    element: <pages.SpinnerPage />,
  },
  {
    path: Route.NODE_MANAGER_PAGE,
    element: <pages.NodeManagerPage />,
  },
  {
    path: Route.CONFIGURATION_MANAGER_PAGE,
    element: <pages.ConfigurationManagerPage />,
  },
  {
    path: Route.USER_MANAGEMENT_PAGE,
    element: <pages.UserManagementPage />,
  },
  {
    path: Route.MACHINES,
    element: <pages.MachinesListPage />,
  },
  {
    path: Route.TICKETS_PAGE,
    element: <pages.TicketsPage />,
  },
  {
    path: Route.FACTORY_DASHBOARD,
    element: <pages.FactoryDashboardPage />,
  },
  {
    path: Route.DASHBOARD_BUILDER,
    element: <pages.DashboardsPage />,
  },
  {
    path: Route.DASHBOARD,
    element: <pages.DashboardViewerPage />,
  },
  {
    path: DASHBOARD_BY_ID_PATH,
    element: <pages.DashboardViewerPage />,
  },
  {
    path: Route.ADMIN_PANEL,
    element: <pages.AdminPanelPage />,
  },
];

const appRoutes = protectedRoutes.map((route) => ({
  ...route,
  element: (
    <LanguageProvider>
      <NavigationWrapper>
        <AuthGuard>{route.element}</AuthGuard>
      </NavigationWrapper>
      {/* <RenderFooter /> */}
      <Taskbar />
    </LanguageProvider>
  ),
}));

// this route is outside of AuthGuard to allow unauthenticated access for support-service over VNC
const vncRoute: RouteObject = {
  path: Route.VNC,
  element: (
    <LanguageProvider>
      <pages.VncPage />
    </LanguageProvider>
  ),
};

const routes: RouteObject = {
  path: "/",
  element: <RootNode />,
  children: [
    {
      path: Route.LOGIN,
      element: <pages.Login />,
    },
    {
      path: Route.LOGOUT,
      element: <pages.Logout />,
    },
    vncRoute,
    ...appRoutes,
    {
      path: "*",
      element: <Navigate to={Route.THINGS_OS_HOMEPAGE} replace />,
    },
  ],
};

export const router = createBrowserRouter([routes]);
